import React from "react";

/**
 * Compact AI Intelligence strip rendered under each widget section.
 *
 * Consumes a widget-scoped slice of the `/api/intelligence/{client}/insights`
 * response — same engine output as the Hero, so the dashboard and the future
 * Executive PDF stay perfectly in sync.
 *
 * Expected `widget` shape:
 *   { summary, opportunity, risk, recommendation }
 */
const Pill = ({ kind, accent, icon, text, testid }) => (
  <div
    className="flex-1 min-w-[220px] p-3 rounded-lg border border-white/10 bg-white/[0.02]"
    style={{ borderTop: `2px solid ${accent}` }}
    data-testid={testid}
  >
    <div className="flex items-center gap-1.5 mb-1.5">
      <span
        className="w-5 h-5 rounded flex items-center justify-center font-bold text-[11px]"
        style={{ background: `${accent}22`, color: accent }}
      >
        {icon}
      </span>
      <span className="text-[9px] uppercase tracking-wider text-white/45">{kind}</span>
    </div>
    <p className="text-[11px] text-white/80 leading-relaxed">{text}</p>
  </div>
);

const WidgetIntelligence = ({ widget, label = "AI Intelligence", testid }) => {
  if (!widget) return null;
  const { summary, opportunity, risk, recommendation } = widget;
  if (!summary && !opportunity && !risk && !recommendation) return null;
  return (
    <div
      className="my-4 p-4 rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.025] to-transparent"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 mb-3">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FFEB00] animate-pulse" />
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#FFEB00]/80">{label}</span>
      </div>
      {summary && (
        <p className="text-[12px] text-white/85 leading-relaxed mb-3 max-w-4xl">{summary}</p>
      )}
      {(opportunity || risk || recommendation) && (
        <div className="flex flex-wrap gap-2">
          {opportunity && <Pill kind="Opportunity" accent="#00E676" icon="↑" text={opportunity} testid={`${testid}-opp`} />}
          {risk && <Pill kind="Risk" accent="#FF6D00" icon="!" text={risk} testid={`${testid}-risk`} />}
          {recommendation && <Pill kind="Recommendation" accent="#FFEB00" icon="→" text={recommendation} testid={`${testid}-rec`} />}
        </div>
      )}
    </div>
  );
};

export default WidgetIntelligence;
